import { createContext, useContext, useState, useMemo } from "react";
import { Company } from "../types/schema";
import { iCompanyContext, iProvider } from "../types";
import { authClient } from "../utils/amplifyClient";
import { useUserContext } from "./UserContext";

export const CompanyContext = createContext<iCompanyContext>({
  updateCompanyInDatabase: async () => { },
  refreshCompany: async () => { },
  isLoading: false,
});

export const useCompanyContext = () => useContext(CompanyContext);

export const CompanyProvider = ({ children }: iProvider) => {
  const { company, setCompany } = useUserContext();
  const [isLoading, setIsLoading] = useState(false);

  const companyModules = {
    trainingModule: company?.trainingModuleEnrollment || false,
    skillTrackingModule: company?.skillTrackingModuleEnrollment || false,
    pmModule: company?.pmModuleEnrollment || false,
    tiresModule: company?.tiresModuleEnrollment || false,
    costTrackingModule: company?.costTrackingModuleEnrollment || false,
  };

  const updateCompanyInDatabase = async (companyId: string, updates: Partial<Company>) => {
    setIsLoading(true);
    try {
      const { data } = await authClient.models.Company.update({ id: companyId, ...updates });
      setCompany(data);
      return data;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCompany = async (companyId: string) => {
    setIsLoading(true);
    try {
      const { data } = await authClient.models.Company.get({ id: companyId });
      setCompany(data as Company);
    } catch (error) {
      console.error('Error refreshing company:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = useMemo(() => ({
    company,
    setCompany,
    ...companyModules,
    updateCompanyInDatabase,
    refreshCompany,
    isLoading,
  }), [company, isLoading]);


  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};
